import { StateStorage } from "zustand/middleware";

// Original
export const basicHashStorage: StateStorage = {
  getItem: (key): string => {
    const searchParams = new URLSearchParams(window.location.hash.slice(1));
    const storedValue = searchParams.get(key) ?? "";
    return JSON.parse(storedValue);
  },
  setItem: (key, newValue): void => {
    const searchParams = new URLSearchParams(window.location.hash.slice(1));
    searchParams.set(key, JSON.stringify(newValue));
    window.location.hash = searchParams.toString();
  },
  removeItem: (key): void => {
    const searchParams = new URLSearchParams(window.location.hash.slice(1));
    searchParams.delete(key);
    window.location.hash = searchParams.toString();
  },
};

// Basic localStorage implementation without encryption
export const basicLocalStorage: StateStorage = {
  getItem: (key): string => {
    const storedValue = localStorage.getItem(key) ?? "";
    if (!storedValue) return "";
    try {
      return JSON.parse(storedValue);
    } catch (e) {
      console.error("Error parsing stored value:", e);
      return "";
    }
  },
  setItem: (key, newValue): void => {
    localStorage.setItem(key, JSON.stringify(newValue));
  },
  removeItem: (key): void => {
    localStorage.removeItem(key);
  },
};

// Simple reversible character swap map
const CHAR_MAP: Record<string, string> = {
  a: "z",
  b: "y",
  c: "x",
  d: "w",
  e: "v",
  f: "u",
  g: "t",
  h: "s",
  i: "r",
  j: "q",
  k: "p",
  l: "o",
  m: "n",
  n: "m",
  o: "l",
  p: "k",
  q: "j",
  r: "i",
  s: "h",
  t: "g",
  u: "f",
  v: "e",
  w: "d",
  x: "c",
  y: "b",
  z: "a",
  "0": "9",
  "1": "8",
  "2": "7",
  "3": "6",
  "4": "5",
  "5": "4",
  "6": "3",
  "7": "2",
  "8": "1",
  "9": "0",
  " ": " ",
  "{": "}",
  "}": "{",
  '"': "'",
  "'": '"',
  ":": ";",
  ";": ":",
};

function simpleSwapEncryptDecrypt(data: string): string {
  return Array.from(data)
    .map((char) => CHAR_MAP[char] || char)
    .join("");
}

const hashStorage: StateStorage = {
  getItem: (key): string => {
    const searchParams = new URLSearchParams(window.location.hash.slice(1));
    const storedValue = searchParams.get(key) ?? "";
    const decrypted = storedValue
      ? simpleSwapEncryptDecrypt(atob(storedValue))
      : "";

    const result = decrypted ? JSON.parse(decrypted) : null;

    return result === null ? null : result;
  },
  setItem: (key, newValue): void => {
    const searchParams = new URLSearchParams(window.location.hash.slice(1));
    const encryptedValue = btoa(
      simpleSwapEncryptDecrypt(JSON.stringify(newValue))
    );
    searchParams.set(key, encryptedValue);
    window.location.hash = searchParams.toString();
  },
  removeItem: (key): void => {
    const searchParams = new URLSearchParams(window.location.hash.slice(1));
    searchParams.delete(key);
    window.location.hash = searchParams.toString();
  },
};

export const localStorageStorage: StateStorage = {
  getItem: (key): string => {
    const storedValue = localStorage.getItem(key) ?? "";
    if (!storedValue) return "";

    try {
      const decrypted = storedValue
        ? simpleSwapEncryptDecrypt(atob(storedValue))
        : "";

      const result = decrypted ? JSON.parse(decrypted) : null;
      return result === null ? null : result;
    } catch (e) {
      console.error("Error decrypting or parsing stored value:", e);
      return "";
    }
  },
  setItem: (key, newValue): void => {
    try {
      const encryptedValue = btoa(
        simpleSwapEncryptDecrypt(JSON.stringify(newValue))
      );
      localStorage.setItem(key, encryptedValue);
    } catch (e) {
      console.error("Error encrypting or storing value:", e);
    }
  },
  removeItem: (key): void => {
    localStorage.removeItem(key);
  },
};

export default hashStorage;
